import React, { useEffect, useMemo, useState } from 'react'
import { addCommas, bindClick } from '@arema/components/Util';
import { Button, Groupper, Header, Message, Stat, Table, Toolbar, useFrontier, Modal } from 'react-frontier2'
import { Link } from 'react-router-dom';
import { ErrorHeader, NotFound } from '../../components';
import { SetLoading } from '@arema/components/Classes';
import { useUser } from '../../AdminHooks';
import API from '../../API';
import moment from 'moment';
import classNames from 'classnames';

interface CorteInfo {
	corte_id: number,
	pdv_id: number,
	pdv_name?: string,
	date_start: number,
	user_start: number,
	user_start_name: string,
	date_end: number,
	user_end: number,
	user_end_name: string,
	total_amount: number,
	total_count: number,
	cancelled: boolean,
	date_cancelled: number,
	orders?: {
		payment_id: number,
		order_id: number,
		order_hash: string,
		delivery_type: number,
		delivery_type_name: string,
		email: string,
		phone: string,
		event_id: number,
		event_name: string,
		tickets: number,
		total: number,
		paid: boolean,
		cancelled: boolean,
	}[],
	payment_methods?: {
		method_id: number,
		method_name: string,
		method_name_internal: string,
		total: number,
		tickets: number,
		commission: number,
	}[]
}

interface CorteEvent{
	event_id: number,
	event_name: string,
	total_tickets: number,
	total_orders: number,
	total: number,
}

enum Modals {
	CONFIRM_CLOSE_CORTE = 100,
}

function PdvCorteDetails(props: { pdv_id: number, params: { corte_id: number } }){
	var { user } = useUser()
	var { Toast } = useFrontier();
	var [corte, setCorte] = useState<CorteInfo>(null);
	var [loadError, setloadError] = useState<string>(null);
	var [showModal, setShowModal] = useState<Modals>(null);

	useEffect(()=>{
		if(!Number.isNaN(props.pdv_id) && !Number.isNaN(props.params.corte_id)){
			loadData();
		}
	}, [props.params.corte_id])

	var loadData = ()=>{
		API.getPDVCorteInfo(props.pdv_id, props.params.corte_id).then((res)=>{
			if(res.error) return setloadError(res.message);
			setCorte(res.data)
		}).catch(()=>{
			setloadError('Error inesperado cargando el corte (LCL-1)');
		});
	}

	var closeCorte = (setLoading: SetLoading)=>{
		setLoading(true);
		API.closePDVCorte(props.pdv_id, props.params.corte_id).then((res)=>{
			if(res.error) return Toast.error(res.message);
			setShowModal(null);
			setCorte(corte=>{
				return {
					...corte,
					user_end_name: user.name,
					date_end: moment().unix()
				}
			})
			return Toast.success('Se ha cerrado el corte de manera correcta')
		}).catch(()=>{
			return Toast.error('Error inesperado cerrando el corte (LCL-2)');
		}).finally(()=>{
			setLoading(false);
		})
	}

	var total_tickets = useMemo(()=>{
		if(!corte || !corte.orders) return 0;
		return corte.orders.filter(a=>!!a.paid).reduce((a,b)=>a+b.total, 0);
	}, [corte]);

	var corte_events = useMemo(()=>{
		if(!corte || !corte.orders) return [];
		var events : CorteEvent[] = [];
		for(var i of corte.orders){
			var ex = events.findIndex(a=>a.event_id===i.event_id);
			if(ex==-1){
				events.push({
					event_id: i.event_id,
					event_name: i.event_name,
					total: i.total,
					total_orders: 1,
					total_tickets: i.tickets,
				})
			}else{
				events[ex].total += i.total;
				events[ex].total_orders += 1;
				events[ex].total_tickets += i.tickets;
			}
		}
		return events;
	}, [corte]);

	if(Number.isNaN(props.params.corte_id)){
		return <NotFound />
	}
	if(loadError){
		return <ErrorHeader error={loadError} />
	}
	if(!corte){
		return <Header loading text='Cargando corte' />
	}

	return (
		<div>
			<div style={{ maxWidth: 500, margin: 'auto' }}>
				<Stat.Group>
					<Stat label={'Ingresos'} value={addCommas(total_tickets)} />
					<Stat label={'Ventas'} value={addCommas(corte.orders.length, false)} />
				</Stat.Group>
			</div>
			<Groupper fitted style={{ maxWidth: 400, margin: 'auto' }} title="Detalles">
				{!corte.date_end && (
					<Toolbar fitted style={{ marginBottom: 0, borderBottom: 0 }} >
						<Toolbar.Item iconName="cut" text='Cerrar Corte' color='black' onClick={bindClick(setShowModal, Modals.CONFIRM_CLOSE_CORTE)} /> 
					</Toolbar>
				)}

				<Table fitted striped details className='last'>
					<Table.Body>
						<Table.Row data={['ID Corte', corte.corte_id]} />
						<Table.Row data={['Estatus',
							<div className={classNames('fr label', {
								red: corte.cancelled,
								blue: !corte.cancelled && !corte.date_end,
								orange: !corte.cancelled && !!corte.date_end
							})}>
								{corte.cancelled ? 'Cancelado' : (corte.date_end ? 'Cerrado' : 'Activo')}
							</div>
						]} />
						<Table.Divider />
						<Table.Row data={['Fecha abierto', moment.unix(corte.date_start).format('DD/MM/YY HH:mm:ss')]} />
						<Table.Row>
							<Table.Cell>Usuario creador</Table.Cell>
							<Table.Cell>
								{corte.user_start_name}
								<div className="meta">
									<Button as={Link} to={`/users/${corte.user_start}`} target='_blank' icon size='tiny' iconName='external-link' />
								</div>
							</Table.Cell>
						</Table.Row>
						{!!corte.date_end && <>
							<Table.Divider />
							<Table.Row data={['Fecha Cierre', moment.unix(corte.date_end).format('DD/MM/YY HH:mm:ss')]} />
							<Table.Row>
								<Table.Cell>Usuario cierre</Table.Cell>
								<Table.Cell>
									{corte.user_end_name}
									<div className="meta">
										<Button as={Link} to={`/users/${corte.user_end}`} target='_blank' icon size='tiny' iconName='external-link' />
									</div>
								</Table.Cell>
							</Table.Row>
						</>}
					</Table.Body>
				</Table>
			</Groupper>

			<Table style={{ maxWidth: 600, margin: 'auto', marginTop: 20 }}>
				<Table.Head
					title='Ordenes'
					data={[['ID', 'Evento', 'Estatus', 'Boletos', 'Total']]}
					centeredIndexes={[2, 3, 4]}
					collapsingIndexes={[0, 2, 3, 4]}
				/>
				<Table.Body>
					{corte.orders.length===0 && (
						<Table.Cell empty row colSpan={100}>
							No hay ventas de este corte
						</Table.Cell>
					)}
					{corte.orders.map(ord=>(
						<Table.Row
							selectable
							as={Link}
							key={ord.order_id}
							centeredIndexes={[2, 3, 4]}
							collapsingIndexes={[0, 2, 3, 4]}
							to={`/orders/@${ord.order_id}`}
							target={'_blank'}
							compact
							data={[
								ord.order_id,
								ord.event_name,
								<div className={classNames('fr label', {
									red: ord.cancelled,
									green: !ord.cancelled && ord.paid,
									blue: !ord.cancelled && !ord.paid,
								})}>
									{ord.cancelled ? 'Cancelado' : (ord.paid ? 'Pagado' : 'Pendiente')}
								</div>,
								ord.tickets,
								addCommas(ord.total)
							]}
						/>)
					)}
				</Table.Body>
			</Table>

			{!!corte.payment_methods && corte.payment_methods.length>0 && (
				<Table style={{ maxWidth: 600, margin: 'auto', marginTop: 20 }}>
					<Table.Head
						collapsingIndexes={[0, 2, 3]}
						centeredIndexes={[2]}
						title='Formas de pago'
						data={[['ID', 'Forma de pago', 'Boletos', 'Total']]}
					/>
					<Table.Body>
						{corte.payment_methods.map(a=>(
							<Table.Row collapsingIndexes={[0, 2, 3]} centeredIndexes={[2]} data={[
								a.method_id,
								a.method_name_internal,
								addCommas(a.tickets, false),
								addCommas(a.total)
							]} />
						))}
					</Table.Body>
				</Table>
			)}
			

			{corte_events.length>0 && (
				<Table style={{ maxWidth: 600, margin: 'auto', marginTop: 20 }}>
					<Table.Head
						title='Eventos Vendidos'
						centeredIndexes={[2, 3]}
						collapsingIndexes={[0, 2, 3, 4]}
						data={[['ID', 'Evento', 'Ordenes', 'Boletos', 'Total']]}
					/>
					<Table.Body>
						{corte_events.map(ev=>(
							<Table.Row
								centeredIndexes={[2, 3, 4]}
								data={[ev.event_id, ev.event_name, addCommas(ev.total_orders, false), addCommas(ev.total_tickets, false), addCommas(ev.total)]}
								key={ev.event_id}
							/>)
						)}
					</Table.Body>
				</Table>
			)}

			<Modal open={showModal === Modals.CONFIRM_CLOSE_CORTE} onClose={bindClick(setShowModal)} size="mini">
				<Modal.Header>Cerrar Corte</Modal.Header>
				<Modal.Content>
					<Header text={'¿Cerrar este corte?'} />
					<Message type='info' centered>
						Al cerrar el corte no se podrán crear mas ventas en este corte y el corte no podrá ser abierto nuevamente
					</Message>
					<Table details style={{ marginTop: 15 }}>
						<Table.Head
							title={`Corte #${corte.corte_id}`}
							titleSize='small'
						/>
						<Table.Body data={[
							['ID', corte.corte_id],
							['Fecha inicio', moment.unix(corte.date_start).format('YYYY-MM-DD HH:mm')],
							['Usuario creador', corte.user_start_name]
						]} />
					</Table>
				</Modal.Content>
				<Modal.Actions>
					<Button onClick={bindClick(setShowModal)} text="Cancelar" basic />
					<Button text="Cerrar corte" color="black" onClick={closeCorte} />
				</Modal.Actions>
			</Modal>
		</div>
	)
}

export default PdvCorteDetails